import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { getSubscriptionDetails } from '../services/subscriptionService';
import './SubscriptionConfirmation.css';

function SubscriptionConfirmation() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get subscription ID from URL params
        const params = new URLSearchParams(location.search);
        const subscriptionId = params.get('id');
        
        if (!subscriptionId) {
          throw new Error('No subscription ID provided');
        }
        
        // Fetch subscription details
        const subscriptionData = await getSubscriptionDetails(subscriptionId);
        setSubscription(subscriptionData);
        
        // Track subscription conversion after successful data load
        if (typeof window.gtag !== 'undefined') {
          // Get UTM parameters if they exist
          const urlParams = new URLSearchParams(window.location.search);
          const utmData = {
            utm_source: urlParams.get('utm_source'),
            utm_medium: urlParams.get('utm_medium'),
            utm_campaign: urlParams.get('utm_campaign'),
            utm_term: urlParams.get('utm_term'),
            utm_content: urlParams.get('utm_content')
          };

          // Parse price from string (e.g., "$199/month" -> 199)
          const priceValue = subscriptionData.price ? parseFloat(subscriptionData.price.replace(/[^0-9.]/g, '')) : 0;

          // Track subscription as conversion
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17465397731/POpFCI6RsYUbEOOjk4hB',
            'value': priceValue,
            'currency': 'USD',
            'transaction_id': subscriptionData.id || Date.now().toString(),
            'custom_parameters': {
              'subscription_package': subscriptionData.package_name,
              'has_trial': subscriptionData.has_trial || false,
              'source': utmData.utm_source || 'direct',
              'medium': utmData.utm_medium || 'none',
              'campaign': utmData.utm_campaign || 'none',
              'subscription_completed': true
            }
          });

          // Track as purchase event for GA4
          window.gtag('event', 'purchase', {
            transaction_id: subscriptionData.id || Date.now().toString(),
            value: priceValue,
            currency: 'USD',
            items: [{
              item_id: subscriptionData.id || 'subscription',
              item_name: subscriptionData.package_name || 'Bookkeeping Subscription',
              category: 'subscription',
              quantity: 1,
              price: priceValue
            }]
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading subscription confirmation:', error);
        setError('Failed to load subscription details. Please contact support.');
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search]);

  if (loading) {
    return <div className="loading-container">Loading confirmation details...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button className="retry-button" onClick={() => navigate('/profile')}>
          Go to Profile
        </button>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="confirmation-page">
      <div className="container">
        <div className="confirmation-content">
          <div className="confirmation-header">
            <div className="success-icon">✓</div>
            <h1>Subscription Confirmed!</h1>
            
            {subscription.has_trial ? (
              <p className="confirmation-message">
                Your free trial has been successfully set up. You can start using our services right away!
              </p>
            ) : (
              <p className="confirmation-message">
                Your subscription has been successfully processed. You can start using our services right away!
              </p>
            )}
          </div>
          
          <div className="confirmation-details">
            <div className="confirmation-card">
              <h2>Subscription Details</h2>
              <div className="detail-item">
                <span className="detail-label">Package:</span>
                <span className="detail-value">{subscription.package_name}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Subscription ID:</span>
                <span className="detail-value">{subscription.id}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className="detail-value status-active">Active</span>
              </div>
              
              {subscription.has_trial && (
                <>
                  <div className="detail-item">
                    <span className="detail-label">Trial Period:</span>
                    <span className="detail-value">{subscription.trial_period} days</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Trial End Date:</span>
                    <span className="detail-value">{formatDate(subscription.trial_end_date)}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">First Billing Date:</span>
                    <span className="detail-value">{formatDate(subscription.trial_end_date)}</span>
                  </div>
                </>
              )}
              
              <div className="detail-item">
                <span className="detail-label">Regular Price:</span>
                <span className="detail-value">{subscription.price}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Billing Cycle:</span>
                <span className="detail-value">Monthly</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Payment Method:</span>
                <span className="detail-value">Card ending in {subscription.payment_method.last4}</span>
              </div>
            </div>
            
            <div className="confirmation-info">
              <h3>What's Next?</h3>
              <p>
                Our team will reach out to you shortly to help you get started with your bookkeeping services.
                You can expect an email within the next 24 hours with more information.
              </p>
              
              {subscription.has_trial && (
                <div className="trial-info">
                  <h4>About Your Trial</h4>
                  <p>
                    You won't be charged during your trial period. To avoid being charged after your trial ends, 
                    you can cancel anytime before {formatDate(subscription.trial_end_date)}.
                  </p>
                </div>
              )}
              
              <div className="support-info">
                <h4>Need Help?</h4>
                <p>
                  If you have any questions or need assistance, please contact our support team at 
                  <a href="mailto:support@rkubeservices.com"> support@rkubeservices.com</a>.
                </p>
              </div>
            </div>
          </div>
          
          <div className="confirmation-actions">
            <Link to="/profile" className="btn btn-primary">Go to Profile</Link>
            <Link to="/dashboard" className="btn btn-secondary">Go to Dashboard</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionConfirmation; 